"use client";

import Script from "next/script";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "@/redux/store";
import { isCookiebotMarketingConsentGiven } from "@/utils/analytics/cookiebot";
import {
  buildLiveChatBusinessContext,
  startLiveChatBusinessContextSync,
} from "@/utils/liveChatBusinessContext";
import { reportToFlare } from "@/utils/reportToFlare";

const LIVECHAT_MOUNT_BATCH_DELAY_MS = 50;
const LIVECHAT_LICENSE = process.env.NEXT_PUBLIC_LIVECHAT_LICENSE?.trim();

export function LiveChatSupport() {
  const [shouldLoadLiveChat, setShouldLoadLiveChat] = useState(false);
  const hasReportedContextSyncError = useRef(false);
  const pathname = usePathname();
  const selectedBusiness = useSelector(
    (state: RootState) => state.reviewTrustPilotReducer.selectedCompany,
  );
  const liveChatBusinessContext = useMemo(
    () => buildLiveChatBusinessContext(pathname, selectedBusiness),
    [pathname, selectedBusiness],
  );

  useEffect(() => {
    if (!LIVECHAT_LICENSE) return;

    if (!shouldLoadLiveChat) {
      return;
    }

    return startLiveChatBusinessContextSync({
      context: liveChatBusinessContext,
      getWidget: () => window.LiveChatWidget,
      isConsentGranted: isCookiebotMarketingConsentGiven,
      onSyncError: (error) => {
        if (hasReportedContextSyncError.current) {
          return;
        }

        hasReportedContextSyncError.current = true;
        reportToFlare(error, "livechat_business_context_sync_failed", {
          callback_platform: liveChatBusinessContext.callback_platform,
          callback_page_type: liveChatBusinessContext.callback_page_type,
        });
      },
    });
  }, [liveChatBusinessContext, shouldLoadLiveChat]);

  useEffect(() => {
    if (!LIVECHAT_LICENSE) return;

    // Two-phase load: record user intent, then mount only when Cookiebot
    // marketing consent is granted. Consent revocation hides the loaded widget
    // and clears pending timers so it cannot remount after opt-out.
    let hasLoaded = false;
    let hasIntentToLoad = false;
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
    let mountTimer: ReturnType<typeof setTimeout> | null = null;
    let isPendingMount = false;
    const createdHints: HTMLLinkElement[] = [];
    const passiveOptions: AddEventListenerOptions = { passive: true };

    const addPreconnectHints = () => {
      if (createdHints.length > 0) {
        return;
      }

      const preconnectCDN = document.createElement("link");
      preconnectCDN.rel = "preconnect";
      preconnectCDN.href = "https://cdn.livechatinc.com";
      document.head.appendChild(preconnectCDN);
      createdHints.push(preconnectCDN);

      const preconnectAPI = document.createElement("link");
      preconnectAPI.rel = "preconnect";
      preconnectAPI.href = "https://api.livechatinc.com";
      document.head.appendChild(preconnectAPI);
      createdHints.push(preconnectAPI);

      const dnsPrefetchCDN = document.createElement("link");
      dnsPrefetchCDN.rel = "dns-prefetch";
      dnsPrefetchCDN.href = "https://cdn.livechatinc.com";
      document.head.appendChild(dnsPrefetchCDN);
      createdHints.push(dnsPrefetchCDN);

      const dnsPrefetchAPI = document.createElement("link");
      dnsPrefetchAPI.rel = "dns-prefetch";
      dnsPrefetchAPI.href = "https://api.livechatinc.com";
      document.head.appendChild(dnsPrefetchAPI);
      createdHints.push(dnsPrefetchAPI);
    };

    function cleanupIntentListeners() {
      // removeEventListener is idempotent; this runs after mount and again
      // during effect cleanup so every exit path leaves no intent listeners.
      window.removeEventListener("scroll", registerLoadIntent);
      window.removeEventListener("mousemove", registerLoadIntent);
      window.removeEventListener("touchstart", registerLoadIntent);
      window.removeEventListener("click", registerLoadIntent);
    }

    const restoreHiddenWidget = () => {
      // LiveChat reverses "hide" with "minimize" or "maximize". Keep the
      // launcher visible after consent re-grant without opening chat.
      window.LiveChatWidget?.call?.("minimize");
    };

    const startLiveChatMount = () => {
      if (!isCookiebotMarketingConsentGiven()) {
        return;
      }

      hasLoaded = true;
      cleanupIntentListeners();

      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
      }

      addPreconnectHints();

      isPendingMount = true;
      // This short delay batches synchronous consent/listener cleanup before
      // React mounts the Script tags. Network scheduling still comes from the
      // lazyOnload strategy, not from this timer.
      mountTimer = setTimeout(() => {
        isPendingMount = false;
        mountTimer = null;
        setShouldLoadLiveChat(true);
      }, LIVECHAT_MOUNT_BATCH_DELAY_MS);
    };

    const forceLiveChatRemount = () => {
      // This is a recovery branch for rare third-party failures where the Script
      // mounted but LiveChatWidget never became available, or another script
      // removed the global before consent was granted again. Keep the consent and
      // pending-mount guards here too so the helper's preconditions stay local if
      // a future caller invokes it directly.
      if (!isCookiebotMarketingConsentGiven() || isPendingMount) {
        return;
      }

      // Reset the recorded mount before immediately scheduling a fresh one;
      // startLiveChatMount sets hasLoaded again and isPendingMount prevents
      // duplicate remounts while the 50ms timer is open.
      hasLoaded = false;
      hasIntentToLoad = true;
      startLiveChatMount();
    };

    const loadLiveChat = () => {
      if (!isCookiebotMarketingConsentGiven()) {
        return;
      }

      if (isPendingMount) {
        return;
      }

      if (window.LiveChatWidget) {
        hasLoaded = true;
        cleanupIntentListeners();
        restoreHiddenWidget();
        return;
      }

      if (hasLoaded) {
        forceLiveChatRemount();
        return;
      }

      if (!hasIntentToLoad) {
        return;
      }

      startLiveChatMount();
    };

    function registerLoadIntent() {
      hasIntentToLoad = true;
      loadLiveChat();
    }

    const handleConsentUpdate = () => {
      if (!isCookiebotMarketingConsentGiven()) {
        // Cookiebot may emit both decline and consent-changed events for one
        // update. The decline path is idempotent, so duplicate calls are safe.
        handleConsentDeclined();
        return;
      }

      registerLoadIntent();
    };

    const handleConsentDeclined = () => {
      cleanupIntentListeners();
      hasIntentToLoad = false;

      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }

      // Close the delayed mount window synchronously so a consent revoke cannot
      // leave setShouldLoadLiveChat(true) queued after the user withdraws
      // consent.
      if (mountTimer) {
        clearTimeout(mountTimer);
        mountTimer = null;
      }
      isPendingMount = false;

      // If LiveChat already loaded, the third-party script cannot be unloaded
      // without a page reload, so hide the widget and prevent future loads.
      // Unmounting the Script tag is only React cleanup at that point; hide()
      // is what removes the already-initialized widget from the page.
      window.LiveChatWidget?.call?.("hide");
      // No widget global means the previous mount never became usable; reset so
      // a future consent grant starts a fresh mount instead of restoring.
      if (!window.LiveChatWidget) {
        hasLoaded = false;
      }
      setShouldLoadLiveChat(false);
    };

    // Load on user interaction
    window.addEventListener("scroll", registerLoadIntent, passiveOptions);
    window.addEventListener("mousemove", registerLoadIntent, passiveOptions);
    window.addEventListener("touchstart", registerLoadIntent, passiveOptions);
    // Click is intentionally not passive; browsers do not benefit from passive
    // click listeners the way they do for scroll and touch listeners.
    window.addEventListener("click", registerLoadIntent);

    window.addEventListener("CookiebotOnLoad", handleConsentUpdate);
    window.addEventListener("CookiebotOnAccept", handleConsentUpdate);
    window.addEventListener("CookiebotOnConsentChanged", handleConsentUpdate);
    window.addEventListener("CookiebotOnDecline", handleConsentDeclined);

    // The fallback records load intent only. loadLiveChat still requires
    // Cookiebot marketing consent before mounting the third-party scripts.
    fallbackTimer = setTimeout(registerLoadIntent, 8000);

    if (isCookiebotMarketingConsentGiven()) {
      registerLoadIntent();
    }

    return () => {
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      if (mountTimer) {
        clearTimeout(mountTimer);
        mountTimer = null;
      }

      hasLoaded = false;
      hasIntentToLoad = false;
      isPendingMount = false;
      cleanupIntentListeners();
      window.removeEventListener("CookiebotOnLoad", handleConsentUpdate);
      window.removeEventListener("CookiebotOnAccept", handleConsentUpdate);
      window.removeEventListener(
        "CookiebotOnConsentChanged",
        handleConsentUpdate,
      );
      window.removeEventListener("CookiebotOnDecline", handleConsentDeclined);
      createdHints.forEach((hint) => hint.remove());
    };
  }, []);

  if (!LIVECHAT_LICENSE || !shouldLoadLiveChat) {
    // Do not render a noscript fallback: it cannot honor Cookiebot consent.
    return null;
  }

  return (
    <>
      <Script strategy="lazyOnload" id="live-chat-integration">
        {`
          window.__lc = window.__lc || {};
          window.__lc.license = ${JSON.stringify(LIVECHAT_LICENSE)};
          window.__lc.integration_name = "manual_channels";
          window.__lc.product_name = "livechat";
          (function(n, t, c) {
            function i(n) {
              return e._h ? e._h.apply(null, n) : e._q.push(n)
            }
            var e = {
              _q: [],
              _h: null,
              _v: "2.0",
              on: function() {
                i(["on", c.call(arguments)])
              },
              once: function() {
                i(["once", c.call(arguments)])
              },
              off: function() {
                i(["off", c.call(arguments)])
              },
              get: function() {
                if (!e._h) throw new Error("[LiveChatWidget] You can't use getters before load.");
                return i(["get", c.call(arguments)])
              },
              call: function() {
                i(["call", c.call(arguments)])
              },
              init: function() {
                var n = t.createElement("script");
                n.async = !0, n.type = "text/javascript", n.src = "https://cdn.livechatinc.com/tracking.js", t.head.appendChild(n)
              }
            };
            !n.__lc.asyncInit && e.init(), n.LiveChatWidget = n.LiveChatWidget || e
          }(window, document, [].slice))
        `}
      </Script>
    </>
  );
}
